import { useState } from 'react'
import DopaLogo from './DopaLogo'

interface DopaRoutineSupportProps {
  type: 'hesitation' | 'skip_pattern' | 'encouragement' | 'milestone'
  context: string
  onClose: () => void
}

const DopaRoutineSupport = ({ type, context, onClose }: DopaRoutineSupportProps) => {
  const [isVisible, setIsVisible] = useState(true)

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  const getDopaMessage = () => {
    switch (type) {
      case 'encouragement':
        return {
          title: "Let's begin this together",
          message: "Just one step at a time. I'm here with you through each moment.",
          action: "You've got this",
          isVoice: true
        }
      
      case 'hesitation':
        return {
          title: "Want me to walk you through the next step?",
          message: "Sometimes it helps to have gentle guidance. No pressure at all.",
          action: "Guide me",
          isVoice: true
        }
      
      case 'skip_pattern':
        return {
          title: "Let's take the pressure off",
          message: "You're being kind to yourself by listening to what you need. Would a smaller routine feel better?",
          action: "Try something gentler",
          isVoice: false
        }
      
      case 'milestone':
        return {
          title: "You're making beautiful progress today",
          message: "Look at the care you're giving yourself. This consistency is something to celebrate.",
          action: "Thank you",
          isVoice: true
        }
      
      default:
        return {
          title: "I'm here with you",
          message: "Whatever you're feeling right now is okay. Take your time.",
          action: "Continue",
          isVoice: false
        }
    }
  }

  const message = getDopaMessage()

  if (!isVisible) return null

  return (
    <div className="fixed bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 z-20 transition-all duration-300">
      <div className="bg-peach-50/30 dark:bg-peach-900/30 backdrop-blur-sm rounded-3xl border border-peach-100/30 dark:border-peach-700/30 p-4 sm:p-6 max-w-sm mx-auto shadow-lg">
        <div className="text-center mb-4 sm:mb-6">
          <div className="mx-auto mb-3 sm:mb-4">
            <DopaLogo size="sm" className="justify-center" withText={false} />
          </div>
          
          <h3 className="text-peach-600 dark:text-peach-300 font-light text-sm sm:text-base mb-2 sm:mb-3">
            {message.title}
          </h3>
          
          <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
            {message.message}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={handleClose}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-peach-100/40 hover:bg-peach-200/40 
                       dark:bg-peach-800/40 dark:hover:bg-peach-700/40
                       text-peach-600 dark:text-peach-300 rounded-2xl text-xs sm:text-sm font-light border border-peach-200/30 dark:border-peach-600/30 hover:border-peach-300/40 dark:hover:border-peach-500/40
                       transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-peach-100/30 dark:focus:ring-peach-700/30"
          >
            {message.action}
          </button>
          
          <button
            onClick={handleClose}
            className="px-3 sm:px-4 py-2 sm:py-3 bg-peach-50/20 dark:bg-peach-900/20 hover:bg-peach-100/30 dark:hover:bg-peach-800/30 
                       text-peach-400 dark:text-peach-500 rounded-2xl text-xs sm:text-sm font-light border border-peach-100/20 dark:border-peach-700/20 hover:border-peach-200/30 dark:hover:border-peach-600/30
                       transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-peach-100/30 dark:focus:ring-peach-700/30"
          >
            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Voice indicator animation */}
      {message.isVoice && (
        <div className="absolute top-4 right-4">
          <div className="flex space-x-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-1 h-3 bg-peach-400 dark:bg-peach-300 rounded-full opacity-60"
                style={{
                  animation: `voiceWave 1.5s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes voiceWave {
          0%, 100% { 
            transform: scaleY(0.5); 
            opacity: 0.6; 
          }
          50% { 
            transform: scaleY(1.2); 
            opacity: 1; 
          }
        }
      `}</style>
    </div>
  )
}

export default DopaRoutineSupport